import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebounceValue } from 'usehooks-ts'

import { DEFAULT_LOCALE, getQuestionTypeLabel } from '../questionFormOptions'
import { useGrantsUtils } from '../../../utils/utils'
import { usePartnerFilter } from '../../../programs/grantsProgramsTable/hooks/usePartnerFilter'

import {
  Badge,
  Button,
  ButtonText,
  Flex,
  Icon,
  SearchFilterList,
  SearchInput,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TablePagination,
  TableProvider,
  TableRow,
  TableSearchAndFilters
} from '@percent/lemonade'
import { useGrantsPermissions } from '@percent/admin-dashboard/app/grants/hooks/useGrantsPermissions'
import { useToast } from '@percent/admin-dashboard/containers/toast/ToastContext'
import { QuestionsTableProps } from './QuestionsTable.types'

export function QuestionsTable({
  data,
  totalResults,
  isLoading,
  nextPage,
  previousPage,
  queryParams,
  setQueryParams,
  onAddQuestion,
  onEditQuestion
}: Readonly<QuestionsTableProps>) {
  const { t } = useTranslation()
  const { formatDate } = useGrantsUtils()
  const toast = useToast()
  const [canCreateForm, canUpdateForm] = useGrantsPermissions(['can_create_form', 'can_update_form'])

  const handleCopyId = (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    navigator.clipboard.writeText(id)
    toast(t('grants.forms.questions.idCopied'), 'success')
  }
  const [searchValue, setSearchValue] = useState(queryParams.search ?? '')
  const [debouncedSearch] = useDebounceValue(searchValue, 500)
  const [questionIdsValue, setQuestionIdsValue] = useState((queryParams.questionIds ?? []).filter(Boolean).join(', '))
  const [debouncedQuestionIds] = useDebounceValue(questionIdsValue, 500)

  const { selectedPartner, partnerNameOptions, onPartnerChange, isPartnerNameFilterLoading, partnerSearch } =
    usePartnerFilter({ queryParams, setQueryParams })

  useEffect(() => {
    setQueryParams({ search: debouncedSearch || undefined })
  }, [debouncedSearch, setQueryParams])

  useEffect(() => {
    const ids = debouncedQuestionIds
      .split(/[,\s]+/)
      .map((id: string) => id.trim())
      .filter(Boolean)
    setQueryParams({ questionIds: ids.length > 0 ? ids : undefined })
  }, [debouncedQuestionIds, setQueryParams])

  const columns: TableColumn[] = [
    {
      id: 'question',
      content: t('grants.forms.questions.column.question'),
      isSortable: false,
      props: { width: '28%' }
    },
    {
      id: 'displayName',
      content: t('grants.forms.questions.column.displayName'),
      isSortable: false,
      props: { width: '14%' }
    },
    { id: 'type', content: t('grants.forms.questions.column.type'), isSortable: false, props: { width: '11%' } },
    { id: 'partner', content: t('grants.forms.questions.column.partner'), isSortable: false, props: { width: '12%' } },
    {
      id: 'required',
      content: t('grants.forms.questions.column.required'),
      isSortable: false,
      props: { width: '10%' }
    },
    { id: 'created', content: t('grants.forms.questions.column.created'), isSortable: false, props: { width: '16%' } },
    { id: 'copyId', content: '', isSortable: false, props: { width: '5%' } }
  ]

  return (
    <TableProvider
      columns={columns}
      emptyTableTitle={t('grants.forms.questions.empty')}
      illustration="no-results"
      isLoading={isLoading}
      totalResults={totalResults}
    >
      <TableSearchAndFilters>
        <Flex w="100%" mr={8}>
          <Flex miw="240">
            <SearchInput
              name="questionSearch"
              value={searchValue}
              handleClearValue={() => setSearchValue('')}
              onChange={e => setSearchValue(e.target.value)}
              placeholder={t('grants.forms.questions.searchPlaceholder')}
              data-testid="question-search-input"
              status="default"
              inputSize="small"
            />
          </Flex>
          <Flex align="center" ml={8} miw="300">
            <SearchInput
              name="questionIdsFilter"
              value={questionIdsValue}
              handleClearValue={() => setQuestionIdsValue('')}
              onChange={e => setQuestionIdsValue(e.target.value)}
              placeholder={t('grants.forms.questions.filterByQuestionIds')}
              data-testid="question-ids-filter-input"
              status="default"
              inputSize="small"
            />
          </Flex>
          <Flex align="center" ml={8}>
            <SearchFilterList
              options={partnerNameOptions}
              isLoading={isPartnerNameFilterLoading}
              selectedValues={selectedPartner ? [selectedPartner] : []}
              onChange={onPartnerChange}
              searchValue={partnerSearch.partnerNameSearch}
              onSearchChange={partnerSearch.onPartnerSearchChange}
              handleClearValue={partnerSearch.onClearPartnerSearchValue}
              onKeyDown={partnerSearch.handleKeyPress}
              onButtonClick={partnerSearch.onPartnersSearchButtonClick}
              testId="partner-search-filter"
              buttonText={t('typography.search')}
              placeholder={t('typography.search')}
              label={t('typography.partner')}
              placement="bottom"
            />
          </Flex>
        </Flex>
        {canCreateForm && (
          <Flex w="100%" justify="flex-end" align="center">
            <Button variant="secondary" size="small" data-testid="add-question-button" onClick={onAddQuestion}>
              {t('grants.forms.questions.addQuestion')}
            </Button>
          </Flex>
        )}
      </TableSearchAndFilters>

      <Table>
        <TableBody>
          {data?.map(question => (
            <TableRow
              key={question.id}
              onClick={canUpdateForm ? () => onEditQuestion(question) : undefined}
              style={{ cursor: canUpdateForm ? 'pointer' : 'default' }}
            >
              <TableCell>
                {question.localisedLabel?.locales?.find(l => l.locale === DEFAULT_LOCALE)?.value ?? question.key}
              </TableCell>
              <TableCell>{question.dashboardLabel ?? '-'}</TableCell>
              <TableCell>{getQuestionTypeLabel(t, question.type)}</TableCell>
              <TableCell>{question.partnerId ?? t('grants.forms.questions.generic')}</TableCell>
              <TableCell>
                <Badge variant={question.required ? 'informative' : 'default'}>
                  {question.required ? t('grants.forms.questions.required') : t('grants.forms.questions.optional')}
                </Badge>
              </TableCell>
              <TableCell>{formatDate(question.createdAt)}</TableCell>
              <TableCell>
                <ButtonText onClick={e => handleCopyId(question.id, e)} data-testid={`copy-question-id-${question.id}`}>
                  <Icon name="copy" size={4} color="gray400" />
                </ButtonText>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TablePagination
        nextPage={nextPage}
        previousPage={previousPage}
        totalResultsText={t('table.results', { count: totalResults ?? 0 })}
      />
    </TableProvider>
  )
}
